import { Box, Chip, Divider, Stack, Typography } from '@mui/material'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import {
  ContributionKind,
  payloadFor,
  type ContributionModerationDto,
  type DescriptionPayload,
  type OperatingDay,
  type OperatingHours,
  type OperatingHoursPayload,
  type OtherPayload,
  type PhotoPayload,
} from '@api/types/contributions'
import { describeTime } from '../../utils/solarHours'

/**
 * What a suggestion proposes, beside what it would replace.
 *
 * The second half is the point. Accepting an hours change replaces a whole document in one click,
 * and a moderator who cannot see the document being replaced is deciding blind — which is the most
 * destructive thing this feature makes possible. Every kind therefore shows current and proposed
 * together, even where the current value is "nothing".
 */
export function ContributionComparison({ row }: { row: ContributionModerationDto }) {
  switch (row.kind) {
    case ContributionKind.Description:
      return <DescriptionComparison row={row} />
    case ContributionKind.OperatingHours:
      return <HoursComparison row={row} />
    case ContributionKind.Photo:
      return <PhotoComparison row={row} />
    case ContributionKind.Other:
      return <OtherNote row={row} />
    default:
      return null
  }
}

function DescriptionComparison({ row }: { row: ContributionModerationDto }) {
  const payload = payloadFor<DescriptionPayload>(row)

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
      <Panel title={`Now — ${fieldLabel(row.targetKey)}`}>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {row.currentValue || <Muted>Nothing yet.</Muted>}
        </Typography>
      </Panel>
      <Panel title="Suggested" accent>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {payload?.proposed || <Muted>Could not read the suggestion.</Muted>}
        </Typography>
      </Panel>
    </Stack>
  )
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

function HoursComparison({ row }: { row: ContributionModerationDto }) {
  const payload = payloadFor<OperatingHoursPayload>(row)
  const proposed = payload?.operatingHours
  const current = parseHours(row.currentValue)

  return (
    <Stack spacing={1.5}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Panel title="Now">
          <HoursGrid hours={current} against={proposed} />
        </Panel>
        <Panel title="Suggested" accent>
          <HoursGrid hours={proposed} against={current} />
        </Panel>
      </Stack>
      {/* The trap this feature has to keep saying out loud: hours are one document, replaced
          whole. A day missing from the right-hand column will be gone after accepting. */}
      <Typography variant="caption" color="warning.main">
        Accepting replaces the whole week. Any day missing on the right will be cleared.
      </Typography>
      {payload?.intentionallyEmpty && (
        <Typography variant="caption" color="text.secondary">
          The contributor marked this as “this place publishes no hours”.
        </Typography>
      )}
    </Stack>
  )
}

/**
 * One week, with the days that differ from the other side marked.
 *
 * Marking rather than only listing: reading two seven-row grids and spotting which line moved is
 * exactly the kind of comparison people get wrong when they are working through a queue.
 */
function HoursGrid({
  hours,
  against,
}: {
  hours?: OperatingHours | null
  against?: OperatingHours | null
}) {
  if (!hours || (hours.weekly ?? []).length === 0) {
    return <Muted>No hours set.</Muted>
  }

  return (
    <Stack spacing={0.25}>
      {DAY_NAMES.map((name, day) => {
        const mine = hours.weekly?.find((d) => d.day === day)
        const theirs = against?.weekly?.find((d) => d.day === day)
        const differs = describeDay(mine) !== describeDay(theirs)

        return (
          <Stack key={day} direction="row" spacing={1} alignItems="baseline">
            <Typography variant="caption" sx={{ width: 78, color: 'text.secondary' }}>
              {name}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: differs ? 700 : 400, color: differs ? 'warning.main' : undefined }}
            >
              {describeDay(mine)}
            </Typography>
          </Stack>
        )
      })}
      {(hours.overrides ?? []).length > 0 && (
        <>
          <Divider sx={{ my: 0.5 }} />
          {hours.overrides.map((o, i) => (
            <Typography key={i} variant="caption" color="text.secondary">
              {o.start}–{o.end}: {o.closed ? 'closed' : `${describeTime(o.open)}–${describeTime(o.close)}`}
              {o.label ? ` (${o.label})` : ''}
            </Typography>
          ))}
        </>
      )}
      {hours.notes && (
        <Typography variant="caption" color="text.secondary">
          {hours.notes}
        </Typography>
      )}
    </Stack>
  )
}

function PhotoComparison({ row }: { row: ContributionModerationDto }) {
  const payload = payloadFor<PhotoPayload>(row)

  return (
    <Stack spacing={1.5}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Panel title="Cover now">
          {row.currentCoverUrl ? (
            <Box
              component="img"
              src={row.currentCoverUrl}
              alt="Current cover"
              sx={{ maxHeight: 220, maxWidth: '100%', objectFit: 'contain', borderRadius: 1 }}
            />
          ) : (
            <Muted>No picture at all.</Muted>
          )}
        </Panel>
        <Panel title="Suggested" accent>
          {row.mediaUrl ? (
            <Box
              component="img"
              src={row.mediaUrl}
              alt="Suggested photo"
              sx={{ maxHeight: 220, maxWidth: '100%', objectFit: 'contain', borderRadius: 1 }}
            />
          ) : (
            <Muted>The photo is missing.</Muted>
          )}
        </Panel>
      </Stack>

      {/* An accepted photo joins the gallery. It only becomes the cover when there is not one
          already, and a moderator should not be surprised by which happened. */}
      <Typography variant="caption" color="text.secondary">
        {row.currentCoverUrl
          ? 'Accepting adds this to the gallery. The cover stays as it is.'
          : 'This adventure has no cover, so accepting will also make this its cover.'}
      </Typography>

      {payload?.caption && (
        <Typography variant="body2">Caption: “{payload.caption}”</Typography>
      )}
      {(payload?.author || payload?.licenseName || payload?.sourceUrl) && (
        <Typography variant="caption" color="text.secondary">
          Credit: {[payload.author, payload.licenseName, payload.sourceUrl]
            .filter(Boolean)
            .join(' · ')}
        </Typography>
      )}
      {row.hadLocationData && (
        // Cheap and useful: a photograph that carried coordinates was taken somewhere, and a
        // contributor passing off a stock image usually sends one that carried none.
        <Chip
          icon={<LocationOnIcon />}
          size="small"
          color="info"
          variant="outlined"
          label="Was taken with location on — coordinates removed before storing"
          // Chip labels never wrap by default; this one is wider than a phone.
          sx={{ alignSelf: 'flex-start', maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }}
        />
      )}
    </Stack>
  )
}

function OtherNote({ row }: { row: ContributionModerationDto }) {
  const payload = payloadFor<OtherPayload>(row)

  return (
    <Stack spacing={1}>
      <Panel title="What they said">
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {payload?.text || <Muted>Could not read the note.</Muted>}
        </Typography>
      </Panel>
      {/* Accepting a free-text note applies nothing — it means "this is legitimate, passing it to
          the owner". Saying so stops a moderator waiting for a change that will not come. */}
      <Typography variant="caption" color="text.secondary">
        Accepting this changes nothing on the adventure. It records the note and tells the owner.
      </Typography>
    </Stack>
  )
}

// ---- small pieces ---------------------------------------------------------------------------

function Panel({
  title,
  accent,
  children,
}: {
  title: string
  accent?: boolean
  children: React.ReactNode
}) {
  return (
    <Box
      sx={{
        flex: 1,
        p: 1.5,
        borderRadius: 1,
        border: 1,
        borderColor: accent ? 'primary.main' : 'divider',
        bgcolor: accent ? 'action.hover' : 'transparent',
        minWidth: 0,
      }}
    >
      <Typography variant="overline" color="text.secondary">
        {title}
      </Typography>
      {children}
    </Box>
  )
}

function Muted({ children }: { children: React.ReactNode }) {
  return (
    <Typography component="span" variant="body2" color="text.disabled" fontStyle="italic">
      {children}
    </Typography>
  )
}

function fieldLabel(targetKey: string): string {
  return targetKey === 'QuickDescription' ? 'short description' : 'full description'
}

function describeDay(day?: OperatingDay | null): string {
  if (!day) return 'not set'
  if (day.closed) return 'closed'
  if (!day.open && !day.close) return 'not set'
  // Either end may follow the sun rather than the clock. Left as the stored
  // token, the difference a moderator is being asked to approve reads as
  // "sunset+30" against "sunset+60" — legible only to whoever wrote the format.
  return `${describeTime(day.open)}–${describeTime(day.close)}`
}

function parseHours(json?: string | null): OperatingHours | null {
  if (!json) return null
  try {
    return JSON.parse(json) as OperatingHours
  } catch {
    return null
  }
}
